import { useState, type JSX, type ReactNode } from "react";
import {
  Button,
  Card,
  ConfirmDialog,
  Inline,
  Stack,
  StatePill,
  TextInput,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

import {
  GTCC_BRANDING_IDLE_CAP_MAX,
  GTCC_BRANDING_IDLE_CAP_MIN,
  GTCC_BRANDING_LOCALES,
  GTCC_BRANDING_PRESETS,
  GTCC_BRANDING_VISIBILITY_MODES,
  brandingPatch,
  brandingSaveError,
  draftErrors,
  draftFrom,
  isBrandingPreset,
  isVisibilityMode,
  logoKind,
  needsConfirmation,
  tenantLabel,
  useGtccBrandingSave,
  type GtccBrandingDraft,
  type GtccBrandingTenant,
} from "./useGtccBranding.js";

/**
 * A-GTCC-27-WRITE2 — the branding editor for ONE target tenant
 * (register row 27, Owner-GO 2026-09-21).
 *
 * All values the brand view lists, editable: logo (as https link only),
 * accent colour, default locale, contact address, timezone, idle cap,
 * unit preset, demo switch, onboarding state and vehicle visibility.
 * The save goes through a confirmation dialog naming the target tenant
 * by NAME and ID before anything is sent; the data flow, the diff and
 * the client-side mirror of the server rules sit in `useGtccBranding.ts`.
 *
 * Logo UPLOAD stays out (own package): there is no file input and no
 * FileReader here, a `data:` URI typed into the link field is refused by
 * the client rule and by the route, and the stored logo is never rendered
 * — the editor reports its kind and offers to replace or remove the link.
 *
 * Switching the visibility TO restricted hides every vehicle from every
 * non-implicit member of that tenant, so the editor asks for the typed
 * tenant name exactly as the tenant's own path does; the server checks it
 * again (`confirmation_mismatch`).
 */

const selectStyle = {
  background: "var(--gt-sys-surface)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
  color: "var(--gt-sys-text)",
  padding: "var(--gt-sys-space-2)",
  inlineSize: "100%",
} as const;

const mutedStyle = {
  margin: 0,
  color: "var(--gt-sys-text-muted)",
  fontSize: "var(--gt-sys-font-size-xs)",
} as const;

const dangerStyle = {
  margin: 0,
  color: "var(--gt-sys-danger)",
  fontSize: "var(--gt-sys-font-size-xs)",
} as const;

function FieldLabel({
  text,
  children,
}: {
  readonly text: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: "block" }}>
      <Stack gap={1}>
        <span style={mutedStyle}>{text}</span>
        {children}
      </Stack>
    </label>
  );
}

function Switch({
  label,
  checked,
  onChange,
  testId,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly testId: string;
}): JSX.Element {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "var(--gt-sys-space-2)" }}>
      <input
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        data-testid={testId}
      />
      <span>{label}</span>
    </label>
  );
}

export function GtccBrandingEditor({
  tenant,
  onClose,
}: {
  readonly tenant: GtccBrandingTenant;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useLocale();
  const save = useGtccBrandingSave();
  const [draft, setDraft] = useState<GtccBrandingDraft>(() => draftFrom(tenant));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  const name = tenantLabel(tenant);
  const errors = draftErrors(tenant, draft);
  const patch = brandingPatch(tenant, draft);
  const changed = Object.keys(patch).length > 0;
  const valid = Object.keys(errors).length === 0;
  const askName = needsConfirmation(tenant, draft);

  const set = <K extends keyof GtccBrandingDraft>(key: K, value: GtccBrandingDraft[K]): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const saveErrorText = save.isError
    ? t(`admin.gtcc.branding.edit.save_error.${brandingSaveError(save.error)}`)
    : null;

  const storedKind = logoKind(tenant.settings?.logoUrl);
  const storedPreset = draftFrom(tenant).preset;
  const storedMode = draftFrom(tenant).visibilityMode;

  return (
    <section aria-labelledby="gtcc-branding-edit-heading" data-testid="gtcc-branding-edit">
      <Card elevation={1}>
        <Stack gap={3}>
          <Stack gap={1}>
            <h3
              id="gtcc-branding-edit-heading"
              style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}
            >
              {t("admin.gtcc.branding.edit.title", { name })}
            </h3>
            <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
              {t("admin.gtcc.branding.edit.subtitle", { id: tenant.id })}
            </p>
          </Stack>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!changed || !valid) return;
              save.reset();
              setConfirmOpen(true);
            }}
          >
            <Stack gap={3}>
              <Stack gap={2}>
                <TextInput
                  label={t("admin.gtcc.branding.edit.logo_link")}
                  hint={t("admin.gtcc.branding.edit.logo_hint")}
                  type="url"
                  value={draft.logoUrl}
                  onChange={(event) => set("logoUrl", event.currentTarget.value)}
                  {...(errors.logoUrl ? { error: t("admin.gtcc.branding.edit.logo_invalid") } : {})}
                  data-testid="gtcc-branding-edit-logo"
                />
                <Inline gap={2} align="center">
                  <StatePill
                    tone={storedKind === "none" ? "neutral" : "info"}
                    label={t(`admin.gtcc.branding.value.logo.${storedKind}`)}
                  />
                  {draft.logoUrl ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => set("logoUrl", "")}
                      data-testid="gtcc-branding-edit-logo-remove"
                    >
                      {t("admin.gtcc.branding.edit.logo_remove")}
                    </Button>
                  ) : null}
                </Inline>
                <p style={mutedStyle}>{t("admin.gtcc.branding.edit.logo_upload_note")}</p>
              </Stack>

              <TextInput
                label={t("admin.gtcc.branding.column.accent")}
                hint={t("admin.gtcc.branding.edit.accent_hint")}
                value={draft.accentColor}
                onChange={(event) => set("accentColor", event.currentTarget.value)}
                {...(errors.accentColor
                  ? { error: t("admin.gtcc.branding.edit.accent_invalid") }
                  : {})}
                data-testid="gtcc-branding-edit-accent"
              />

              <FieldLabel text={t("admin.gtcc.branding.column.locale")}>
                <select
                  value={draft.defaultLocale}
                  onChange={(event) => set("defaultLocale", event.currentTarget.value)}
                  style={selectStyle}
                  data-testid="gtcc-branding-edit-locale"
                >
                  <option value="">{t("admin.gtcc.branding.value.unset")}</option>
                  {GTCC_BRANDING_LOCALES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </FieldLabel>

              <TextInput
                label={t("admin.gtcc.branding.column.contact")}
                type="email"
                value={draft.contactEmail}
                onChange={(event) => set("contactEmail", event.currentTarget.value)}
                {...(errors.contactEmail
                  ? { error: t("admin.gtcc.branding.edit.email_invalid") }
                  : {})}
                data-testid="gtcc-branding-edit-contact"
              />

              <TextInput
                label={t("admin.gtcc.branding.column.timezone")}
                hint={t("admin.gtcc.branding.edit.timezone_hint")}
                value={draft.timezone}
                onChange={(event) => set("timezone", event.currentTarget.value)}
                {...(errors.timezone
                  ? { error: t("admin.gtcc.branding.edit.timezone_invalid") }
                  : {})}
                data-testid="gtcc-branding-edit-timezone"
              />

              <TextInput
                label={t("admin.gtcc.branding.column.idle_cap")}
                hint={t("admin.gtcc.branding.edit.idle_cap_hint", {
                  min: GTCC_BRANDING_IDLE_CAP_MIN,
                  max: GTCC_BRANDING_IDLE_CAP_MAX,
                })}
                type="number"
                inputMode="numeric"
                min={GTCC_BRANDING_IDLE_CAP_MIN}
                max={GTCC_BRANDING_IDLE_CAP_MAX}
                step={1}
                value={draft.sessionIdleCapMinutes}
                onChange={(event) => set("sessionIdleCapMinutes", event.currentTarget.value)}
                {...(errors.sessionIdleCapMinutes
                  ? { error: t("admin.gtcc.branding.edit.idle_cap_invalid") }
                  : {})}
                data-testid="gtcc-branding-edit-idle-cap"
              />

              <FieldLabel text={t("admin.gtcc.branding.column.preset")}>
                <select
                  value={draft.preset}
                  onChange={(event) => set("preset", event.currentTarget.value)}
                  style={selectStyle}
                  data-testid="gtcc-branding-edit-preset"
                >
                  <option value="">{t("admin.gtcc.branding.value.unset")}</option>
                  {GTCC_BRANDING_PRESETS.map((preset) => (
                    <option key={preset} value={preset}>
                      {t(`admin.gtcc.branding.edit.preset.${preset}`)}
                    </option>
                  ))}
                  {storedPreset !== "" && !isBrandingPreset(storedPreset) ? (
                    <option value={storedPreset}>
                      {t("admin.gtcc.branding.edit.preset.stored", { preset: storedPreset })}
                    </option>
                  ) : null}
                </select>
              </FieldLabel>

              <Switch
                label={t("admin.gtcc.branding.edit.demo_switch")}
                checked={draft.demoApplyAllowed}
                onChange={(next) => set("demoApplyAllowed", next)}
                testId="gtcc-branding-edit-demo"
              />

              <Stack gap={1}>
                <Switch
                  label={t("admin.gtcc.branding.edit.onboarding_switch")}
                  checked={draft.onboardingDone}
                  onChange={(next) => set("onboardingDone", next)}
                  testId="gtcc-branding-edit-onboarding"
                />
                <p style={mutedStyle}>{t("admin.gtcc.branding.edit.onboarding_hint")}</p>
              </Stack>

              <Stack gap={2}>
                <FieldLabel text={t("admin.gtcc.branding.column.visibility")}>
                  <select
                    value={draft.visibilityMode}
                    onChange={(event) => set("visibilityMode", event.currentTarget.value)}
                    style={selectStyle}
                    data-testid="gtcc-branding-edit-visibility"
                  >
                    {storedMode !== "" && !isVisibilityMode(storedMode) ? (
                      <option value={storedMode}>
                        {t("admin.gtcc.branding.value.visibility.unknown")}
                      </option>
                    ) : null}
                    {GTCC_BRANDING_VISIBILITY_MODES.map((mode) => (
                      <option key={mode} value={mode}>
                        {t(`admin.gtcc.branding.value.visibility.${mode}`)}
                      </option>
                    ))}
                  </select>
                </FieldLabel>
                {askName ? (
                  <TextInput
                    label={t("admin.gtcc.branding.edit.visibility_confirm")}
                    hint={t("admin.gtcc.branding.edit.visibility_confirm_hint", { name })}
                    value={draft.confirm}
                    onChange={(event) => set("confirm", event.currentTarget.value)}
                    {...(errors.confirm
                      ? { error: t("admin.gtcc.branding.edit.visibility_confirm_missing") }
                      : {})}
                    data-testid="gtcc-branding-edit-visibility-confirm"
                  />
                ) : null}
              </Stack>

              {saved && !changed ? (
                <p role="status" data-testid="gtcc-branding-edit-saved" style={{ margin: 0 }}>
                  {t("admin.gtcc.branding.edit.saved", { name })}
                </p>
              ) : null}

              {errors.preset ? (
                <p role="alert" style={dangerStyle} data-testid="gtcc-branding-edit-preset-invalid">
                  {t("admin.gtcc.branding.edit.preset_invalid")}
                </p>
              ) : null}

              <Inline gap={2}>
                <Button
                  type="submit"
                  disabled={!changed || !valid || save.isPending}
                  data-testid="gtcc-branding-edit-save"
                >
                  {t("admin.gtcc.branding.edit.save")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  data-testid="gtcc-branding-edit-close"
                >
                  {t("admin.gtcc.branding.edit.close")}
                </Button>
              </Inline>
            </Stack>
          </form>
        </Stack>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title={t("admin.gtcc.branding.edit.confirm_title")}
        body={t("admin.gtcc.branding.edit.confirm_body", { name, id: tenant.id })}
        confirmLabel={t("admin.gtcc.branding.edit.confirm")}
        cancelLabel={t("common.cancel")}
        pending={save.isPending}
        errorMessage={saveErrorText}
        testIdPrefix="gtcc-branding-edit-confirm"
        onConfirm={() => {
          save.mutate(
            { tenantId: tenant.id, patch },
            {
              onSuccess: () => {
                setConfirmOpen(false);
                setSaved(true);
              },
            },
          );
        }}
        onCancel={() => {
          if (!save.isPending) {
            save.reset();
            setConfirmOpen(false);
          }
        }}
      />
    </section>
  );
}
